import React, { useState, useEffect } from 'react';
import { teacherAPI, commonAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ChartComponent from '../../components/common/ChartComponent';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const StudentProgress = () => {
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [students, setStudents] = useState([]);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedProgress, setSelectedProgress] = useState(null);

  const commentSchema = Yup.object().shape({
    comment: Yup.string().required('Comment is required'),
    recommendation: Yup.string(),
  });

  useEffect(() => {
    fetchAssignedSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      fetchStudents();
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedStudent && selectedSubject) {
      fetchStudentProgress();
    }
  }, [selectedStudent, selectedSubject]);

  const fetchAssignedSubjects = async () => {
    try {
      const response = await teacherAPI.getAssignedSubjects();
      setAssignedSubjects(response.data);
    } catch (error) {
      toast.error('Failed to load assigned subjects');
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      // In a real app, you would fetch students for the selected subject
      // This is a mock implementation
      const mockStudents = [
        { student_reg: 'STU001', name: 'John Doe', class: '10A' },
        { student_reg: 'STU002', name: 'Jane Smith', class: '10A' },
        { student_reg: 'STU003', name: 'Robert Johnson', class: '10B' },
        { student_reg: 'STU004', name: 'Emily Davis', class: '10B' },
        { student_reg: 'STU005', name: 'Michael Wilson', class: '11A' },
      ];
      setStudents(mockStudents);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentProgress = async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getStudentProgress(selectedStudent, selectedSubject);
      setProgressData(response.data);
    } catch (error) {
      // Mock data for demonstration
      setProgressData({
        student_reg: selectedStudent,
        subject_id: selectedSubject,
        progress: [
          {
            academic_session: '2022-2023',
            term: 'Term 1',
            class: '10A',
            assessment_scores: {
              ca1: 18,
              ca2: 19,
              exam: 55,
              assignment: 8
            },
            total_score: 92,
            created_at: '2022-10-15'
          },
          {
            academic_session: '2022-2023',
            term: 'Term 2',
            class: '10A',
            assessment_scores: {
              ca1: 17,
              ca2: 20,
              exam: 58,
              assignment: 9
            },
            total_score: 96,
            created_at: '2023-01-20'
          },
          {
            academic_session: '2022-2023',
            term: 'Term 3',
            class: '10A',
            assessment_scores: {
              ca1: 19,
              ca2: 18,
              exam: 60,
              assignment: 10
            },
            total_score: 98,
            created_at: '2023-04-10'
          },
          {
            academic_session: '2023-2024',
            term: 'Term 1',
            class: '11A',
            assessment_scores: {
              ca1: 20,
              ca2: 19,
              exam: 58,
              assignment: 9
            },
            total_score: 97,
            created_at: '2023-10-12'
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (values, { setSubmitting, resetForm }) => {
    try {
      // API call to save comment
      toast.success('Comment added successfully');
      setShowCommentModal(false);
      resetForm();
      setSelectedProgress(null);
    } catch (error) {
      toast.error('Failed to save comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getPerformanceTrend = () => {
    if (!progressData) return { labels: [], datasets: [] };

    const labels = progressData.progress.map(p => `${p.academic_session} - ${p.term}`);
    const scores = progressData.progress.map(p => p.total_score);

    return {
      labels,
      datasets: [
        {
          label: 'Total Score',
          data: scores,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.3,
          fill: true,
        }
      ]
    };
  };

  const getSubjectName = () => {
    const subject = assignedSubjects.find(s => s.subject_id == selectedSubject);
    return subject ? subject.subject : 'Unknown Subject';
  };

  const getStudentName = () => {
    const student = students.find(s => s.student_reg === selectedStudent);
    return student ? student.name : 'Unknown Student';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Student Progress Tracking</h1>
          <p className="text-gray-600">Monitor and analyze student performance over time</p>
        </div>
      </div>

      {/* Selection Panel */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Subject *
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => {
                setSelectedSubject(e.target.value);
                setSelectedStudent('');
                setProgressData(null);
              }}
              className="input-field"
              disabled={loading}
            >
              <option value="">Choose a subject</option>
              {assignedSubjects.map((subject) => (
                <option key={`${subject.subject_id}-${subject.class_id}`} value={subject.subject_id}>
                  {subject.subject_name} ({subject.class_name})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Student *
            </label>
            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="input-field"
              disabled={!selectedSubject || loading}
            >
              <option value="">Choose a student</option>
              {students.map((student) => (
                <option key={student.student_reg} value={student.student_reg}>
                  {student.name} ({student.student_reg}) - {student.class}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                if (selectedStudent && selectedSubject) {
                  fetchStudentProgress();
                }
              }}
              disabled={!selectedStudent || !selectedSubject || loading}
              className="w-full btn-primary py-2"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Loading...
                </>
              ) : (
                <>
                  <i className="fas fa-sync mr-2"></i>
                  Refresh Data
                </>
              )}
            </button>
          </div>
        </div>

        {selectedStudent && selectedSubject && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-700">
                  Viewing progress for <strong>{getStudentName()}</strong> in{' '}
                  <strong>{getSubjectName()}</strong>
                </p>
              </div>
              <div className="text-sm text-green-600">
                {progressData?.progress?.length || 0} assessment records found
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      {selectedStudent && selectedSubject && progressData ? (
        <div className="space-y-6">
          {/* Performance Overview */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Performance Overview</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Current Class:</span>
                <span className="font-medium">
                  {progressData.progress[progressData.progress.length - 1]?.class}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Performance Trend Chart */}
              <div className="lg:col-span-2">
                <div className="h-64">
                  <ChartComponent
                    type="line"
                    data={getPerformanceTrend()}
                    options={{
                      scales: {
                        y: {
                          min: 0,
                          max: 100,
                          title: {
                            display: true,
                            text: 'Total Score'
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Performance Stats */}
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Highest Score</div>
                  <div className="text-2xl font-bold text-green-600">
                    {Math.max(...progressData.progress.map(p => p.total_score))}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Average Score</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {(
                      progressData.progress.reduce((acc, p) => acc + p.total_score, 0) /
                      progressData.progress.length
                    ).toFixed(1)}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Improvement</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {(
                      progressData.progress[progressData.progress.length - 1]?.total_score -
                      progressData.progress[0]?.total_score
                    ).toFixed(1)}
                    <span className="text-sm font-normal ml-1">points</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Progress Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Detailed Assessment History</h3>
              <p className="text-gray-600 text-sm mt-1">Complete assessment record across all terms</p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Session & Term
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assessment Scores
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {progressData.progress.map((record, index) => {
                    const grade = record.total_score >= 90 ? 'A+' :
                                 record.total_score >= 80 ? 'A' :
                                 record.total_score >= 70 ? 'B' :
                                 record.total_score >= 60 ? 'C' :
                                 record.total_score >= 50 ? 'D' : 'F';

                    const gradeColor = grade === 'A+' ? 'bg-green-100 text-green-800' :
                                      grade.startsWith('A') ? 'bg-green-100 text-green-800' :
                                      grade.startsWith('B') ? 'bg-blue-100 text-blue-800' :
                                      grade.startsWith('C') ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800';

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {record.academic_session}
                            </div>
                            <div className="text-sm text-gray-500">{record.term}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{record.class}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(record.assessment_scores || {}).map(([key, value]) => (
                              <div key={key} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                {key.toUpperCase()}: {value}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-lg font-bold text-gray-900">
                            {record.total_score.toFixed(1)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${gradeColor}`}>
                            {grade}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <button
                            onClick={() => {
                              setSelectedProgress(record);
                              setShowCommentModal(true);
                            }}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <i className="fas fa-comment mr-1"></i> Add Comment
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Performance Analysis */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Strengths</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-green-600">
                    <i className="fas fa-check-circle mr-2"></i>
                    Consistent improvement across terms
                  </li>
                  <li className="flex items-center text-green-600">
                    <i className="fas fa-check-circle mr-2"></i>
                    Excellent exam performance
                  </li>
                  <li className="flex items-center text-green-600">
                    <i className="fas fa-check-circle mr-2"></i>
                    Strong assignment submission quality
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Areas for Improvement</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-yellow-600">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Could improve CA1 scores
                  </li>
                  <li className="flex items-center text-yellow-600">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Needs more consistency in classwork
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <i className="fas fa-chart-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select Student and Subject</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Please select a subject and student from the dropdown menus above to view their progress data.
          </p>
        </div>
      )}

      {/* Comment Modal */}
      <Modal
        isOpen={showCommentModal}
        onClose={() => {
          setShowCommentModal(false);
          setSelectedProgress(null);
        }}
        title="Add Progress Comment"
      >
        <Formik
          initialValues={{
            comment: '',
            recommendation: '',
          }}
          validationSchema={commentSchema}
          onSubmit={handleAddComment}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              {selectedProgress && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="text-sm text-gray-600">
                    Commenting on: <strong>{selectedProgress.academic_session} - {selectedProgress.term}</strong>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Total Score: <strong>{selectedProgress.total_score}</strong>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comment *
                </label>
                <Field
                  as="textarea"
                  name="comment"
                  rows={4}
                  className="input-field"
                  placeholder="Enter your observations and feedback..."
                />
                {errors.comment && touched.comment && (
                  <div className="text-red-500 text-sm mt-1">{errors.comment}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recommendations
                </label>
                <Field
                  as="textarea"
                  name="recommendation"
                  rows={2}
                  className="input-field"
                  placeholder="Suggest areas for improvement..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCommentModal(false);
                    setSelectedProgress(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Save Comment
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default StudentProgress;